import { getTransport } from '../transport'
import { apiBases } from '../api-bases'
import { getScope } from './pull-engine'
import { MetaRepository } from '../../repositories/MetaRepository'
import { cacheOrgConfig, warmOrgConfigKey } from '../../domain/catalogs'
import { dbg, dbgWarn } from '../../logger'

/**
 * pull/org-config.js — WARM-UP del config de organización (ap_categories / ap_categories2).
 *
 * Esos dos catálogos alimentan Categoría y Área del diálogo "Agregar tarea", pero NO son un
 * descriptor Tier B (vienen de GET organizations/<id>/config/?type=<t>, un blob de config, no
 * una lista paginada con tabla c_*). Sin este warm-up, el cache en `meta` solo se poblaría al
 * abrir el diálogo estando online — un operador que instala, se loguea y se va a campo sin
 * abrirlo nunca, quedaría con selects vacíos offline. El tick del sync-worker (ya gated por
 * flag + isReallyOnline REAL) los baja best-effort con TTL propio.
 *
 * Red vía getTransport() (costura PUSH-05: en offline/sync no se importan services de la app);
 * escritura vía domain/catalogs.cacheOrgConfig (mismo formato que el read-through del store,
 * así el diálogo lee un único cache sin importar quién lo calentó).
 */

// Alineado a TIER_B_TTLS (users/areas/hierarchies = 12h): config de org cambia poco.
export const WARM_ORG_CONFIG_TTL = 12 * 3600000
export const WARM_TYPES = ['ap_categories', 'ap_categories2']
// suppressConnectionToast: este GET corre en el tick background del worker — un Network Error
// a media descarga NO debe brincarle un toast rojo a un usuario que no hizo nada.
const WARM_FETCH_CONFIG = { timeout: 30000, suppressConnectionToast: true }

// El interceptor (api/responser.js) RESUELVE como valor los errores sin `.response` que no son
// 'Network Error' (p.ej. timeout) — un body válido es un objeto CON la clave `data` (incluida
// `{data:null}`: respuesta legítima de una org sin ese type configurado, que cacheOrgConfig
// también acepta; así la frescura por marca basta y no se re-descarga cada tick).
const isValidBody = b => !!b && typeof b === 'object' && 'data' in b

/**
 * Baja y cachea ap_categories + ap_categories2 si el warm está vencido. Best-effort: nunca
 * lanza; devuelve un resumen diagnosticable.
 * @returns {Promise<{skipped?:string, warmed?:string[]}>}
 */
export async function warmOrgConfigs ({ now = Date.now() } = {}) {
  try {
    const scope = getScope(undefined)
    const orgId = scope && scope.organizationId
    if (!orgId) return { skipped: 'no-scope' }

    // Frescura por marca (marca y cache viven AMBAS en `meta`: una barrida se lleva a las dos,
    // así que la marca sola es un proxy fiable de "cache poblado").
    const mark = Number(await MetaRepository.get(warmOrgConfigKey(orgId))) || 0
    if (now - mark < WARM_ORG_CONFIG_TTL) return { skipped: 'fresh' }

    const { gql } = await apiBases()
    const t = getTransport()
    const warmed = []
    for (const type of WARM_TYPES) {
      const res = await t.get(`${gql}organizations/${orgId}/config/`, { ...WARM_FETCH_CONFIG, params: { type } })
      const body = res && res.data
      // Un body inválido (timeout resuelto por el interceptor) NO cuenta como warmed: sin esto,
      // la marca de frescura se escribía con el cache a medias y el operador cold-start quedaba
      // con selects vacíos 12h.
      if (!isValidBody(body)) {
        dbgWarn('sync', `org-config warm: respuesta inválida para ${type} (reintenta próximo tick)`)
        return { skipped: 'invalid-response' }
      }
      await cacheOrgConfig(orgId, type, body)
      warmed.push(type)
    }
    // La marca SOLO si ambos bajaron OK (fallo o respuesta inválida salen antes sin marcar).
    await MetaRepository.put(warmOrgConfigKey(orgId), String(now))
    dbg('sync', `org-config warm OK (org ${orgId}: ${warmed.join(', ')})`)
    return { warmed }
  } catch (error) {
    dbgWarn('sync', 'org-config warm failed (reintenta en el próximo tick)', error)
    return { skipped: 'error' }
  }
}
